import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Center,
  HStack,
  InputGroup,
  InputLeftAddon,
  NumberInput,
  NumberInputField,
  Spacer,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { ReactComponent as IconCheck } from "../../../assets/icons/check.svg";
import { ReactComponent as IconClose } from "../../../assets/icons/xClose.svg";
import { ReactComponent as IconTrash } from "../../../assets/icons/trash01.svg";
import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo, useRef, useState } from "react";
import { DataTable } from "../../../components/data-table";
import { MultiSelect, MultiSelectOption } from "../../../components/select";
import Tooltip from "../../../components/tooltip";
import { useContextTypeSettingsOrganization } from "../dashboard-layout";
import { useTransactionCategories } from "../../../hooks/use-transaction-categories";
import {
  ApprovalRule,
  ApprovalRuleTriggerType,
  CreateApprovalRuleMutation,
  UpdateApprovalRuleMutation,
} from "../../../API";
import { createApprovalRule, updateApprovalRule } from "../../../graphql/mutations";
import { API } from "aws-amplify";
import { GraphQLQuery } from "@aws-amplify/api";
import { useQueryClient } from "@tanstack/react-query";
import { approvalKeys } from "../../../hooks/approvals/query-keys";

// ApprovalRule already has deletedAt — satisfies DataTable's SortModel constraint
type RuleRow = ApprovalRule;

const columnHelper = createColumnHelper<RuleRow>();

type ApprovalRulesTableProps = {
  rules: ApprovalRule[];
  isActiveTab: boolean;
  hasApprovers: boolean;
};

// Generate a German summary of the rule's conditions
const generateSummary = (
  rule: ApprovalRule,
  getBookingAccountName: (id: string) => string,
  totalBookingAccountCount: number
): string => {
  const parts: string[] = [];

  const accountIds = rule.bookingAccountIds ?? [];
  if (accountIds.length > 0) {
    if (accountIds.length >= totalBookingAccountCount) {
      parts.push("Alle Buchungskonten");
    } else {
      const names = accountIds.map((id) => getBookingAccountName(id));
      parts.push(`Buchungskonto: ${names.join(", ")}`);
    }
  }

  if (rule.amountThreshold !== null && rule.amountThreshold !== undefined) {
    parts.push(`Über CHF ${rule.amountThreshold}`);
  }

  if (parts.length === 0) return "Keine Bedingungen (alle Ausgaben)";
  return parts.join(" und ");
};

const ApprovalRulesTable = ({
  rules,
  isActiveTab,
  hasApprovers,
}: ApprovalRulesTableProps) => {
  const { bookkeepings, selectedOrganization } = useContextTypeSettingsOrganization();
  const { transactionCategories } = useTransactionCategories();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>();

  const hasMultipleBookkeepings = (bookkeepings?.length ?? 0) > 1;

  const invalidateRules = () => {
    queryClient.invalidateQueries({ queryKey: approvalKeys.rules(selectedOrganization?.id) });
  };

  const handleUpdateRule = async (ruleId: string, updates: Record<string, unknown>) => {
    try {
      await API.graphql<GraphQLQuery<UpdateApprovalRuleMutation>>({
        query: updateApprovalRule,
        variables: { input: { id: ruleId, ...updates } },
      });
      invalidateRules();
    } catch {
      toast({ title: "Fehler beim Aktualisieren", status: "error", duration: 3000 });
    }
  };

  // Map bookkeepings to MultiSelect options
  const bookkeepingOptions: MultiSelectOption[] = useMemo(
    () =>
      bookkeepings?.map((b) => ({
        label: b.name,
        value: b.id,
      })) ?? [],
    [bookkeepings]
  );

  // Map booking accounts from bookkeepings for the MultiSelect
  const bookingAccountOptions: MultiSelectOption[] = useMemo(
    () =>
      transactionCategories?.map((c) => ({
        label: c.name,
        value: c.id,
      })) ?? [],
    [transactionCategories]
  );

  // Helper to get booking account name by ID
  const getBookingAccountName = useCallback(
    (id: string): string => {
      const cat = transactionCategories?.find((c) => c.id === id);
      return cat?.name ?? id;
    },
    [transactionCategories]
  );

  // Get the currently selected rule
  const selectedRule =
    selectedIndex !== undefined ? rules[selectedIndex] : undefined;

  // Handle remove click — show confirmation dialog
  const handleRemoveClick = useCallback(() => {
    onOpen();
  }, [onOpen]);

  const handleDeleteConfirm = async () => {
    if (selectedRule) {
      await handleUpdateRule(selectedRule.id, { deletedAt: new Date().toISOString() });
      setSelectedIndex(undefined);
    }
    onClose();
  };

  // Handle activate/deactivate for selected rule
  const handleToggleActive = useCallback(async () => {
    if (selectedRule) {
      await handleUpdateRule(selectedRule.id, { isActive: !selectedRule.isActive });
      setSelectedIndex(undefined);
    }
  }, [selectedRule]);

  // Build columns dynamically based on whether org has multiple bookkeepings
  const columns = useMemo(() => {
    const cols = [];

    // Bookkeeping column — only when org has >1 bookkeeping
    if (hasMultipleBookkeepings) {
      cols.push(
        columnHelper.accessor("bookkeepingIds", {
          header: "Buchhaltung",
          cell: (info) => {
            const rule = info.row.original;
            const ruleBookkeepingIds = rule.bookkeepingIds ?? [];
            // Empty/null = org-wide → show all selected (matches "Alle" semantic)
            const selectedOptions: MultiSelectOption[] =
              ruleBookkeepingIds.length === 0
                ? bookkeepingOptions
                : bookkeepingOptions.filter((opt) =>
                    ruleBookkeepingIds.includes(String(opt.value))
                  );

            return (
              <Box minW="180px">
                <MultiSelect
                  options={bookkeepingOptions}
                  value={selectedOptions}
                  allLabel="Alle Buchhaltungen"
                  placeholder="Alle Buchhaltungen"
                  onChange={(newValue) => {
                    // All or none selected = org-wide → store empty array
                    const allOrNone =
                      newValue.length === bookkeepingOptions.length ||
                      newValue.length === 0;
                    const newIds = allOrNone
                      ? []
                      : newValue.map((v) => String(v.value)).sort();
                    const currentIds = [...ruleBookkeepingIds].sort();
                    if (
                      newIds.length === currentIds.length &&
                      newIds.every((id, i) => id === currentIds[i])
                    ) {
                      return; // no change
                    }
                    handleUpdateRule(rule.id, { bookkeepingIds: newIds });
                  }}
                />
              </Box>
            );
          },
        })
      );
    }

    // Booking accounts column
    cols.push(
      columnHelper.accessor("bookingAccountIds", {
        header: "Buchungskonten",
        cell: (info) => {
          const rule = info.row.original;
          const accountIds = rule.bookingAccountIds ?? [];
          const selectedOptions: MultiSelectOption[] = bookingAccountOptions.filter(
            (opt) => accountIds.includes(String(opt.value))
          );

          return (
            <Box minW="180px">
              <MultiSelect
                options={bookingAccountOptions}
                value={selectedOptions}
                placeholder="Buchungskonto auswählen"
                allLabel="Alle Buchungskonten"
                onChange={(newValue) => {
                  handleUpdateRule(rule.id, {
                    bookingAccountIds: newValue.map((v) => String(v.value)),
                  });
                }}
              />
            </Box>
          );
        },
      })
    );

    // Amount column
    cols.push(
      columnHelper.accessor("amountThreshold", {
        header: "Betrag",
        cell: (info) => {
          const rule = info.row.original;
          return (
            <InputGroup size="sm" minW="160px">
              <InputLeftAddon>CHF</InputLeftAddon>
              <NumberInput
                defaultValue={rule.amountThreshold ?? ""}
                precision={2}
                onBlur={(e) => {
                  const parsed = parseFloat(e.target.value);
                  const newValue = isNaN(parsed) ? null : parsed;
                  // Skip when value unchanged (e.g. user tabs through)
                  if (newValue === (rule.amountThreshold ?? null)) return;
                  handleUpdateRule(rule.id, { amountThreshold: newValue });
                }}
                flex={1}
              >
                <NumberInputField
                  borderLeftRadius={0}
                  placeholder="Maximalbetrag eingeben"
                />
              </NumberInput>
            </InputGroup>
          );
        },
      })
    );

    // Summary column
    cols.push(
      columnHelper.display({
        id: "summary",
        header: "Zusammenfassung",
        cell: (info) => (
          <Text fontSize="sm" color="gray.600">
            {generateSummary(
              info.row.original,
              getBookingAccountName,
              bookingAccountOptions.length
            )}
          </Text>
        ),
      })
    );

    return cols;
  }, [
    hasMultipleBookkeepings,
    bookkeepingOptions,
    bookingAccountOptions,
    getBookingAccountName,
  ]);

  const ruleRows: RuleRow[] = rules;

  // Handle adding a new rule via mutation
  const handleAddRule = async () => {
    if (!selectedOrganization) return;
    try {
      await API.graphql<GraphQLQuery<CreateApprovalRuleMutation>>({
        query: createApprovalRule,
        variables: {
          input: {
            triggerType: ApprovalRuleTriggerType.amountThreshold,
            amountThreshold: null,
            bookingAccountIds: [],
            isActive: true,
            organizationId: selectedOrganization.id,
            bookkeepingIds: [],
            authGroupsRead: selectedOrganization.authGroupsRead,
            authGroupsUpdate: selectedOrganization.authGroupsUpdate,
          },
        },
      });
      invalidateRules();
    } catch {
      toast({ title: "Fehler beim Erstellen", status: "error", duration: 3000 });
    }
  };

  return (
    <VStack alignItems="start" width="full" spacing="16px">
      {rules.length === 0 ? (
        <Center py="40px" width="full">
          <Text fontSize={14} color="gray.500">
            Keine Regeln vorhanden. Fügen Sie eine Regel hinzu, um den
            Freigabeprozess zu konfigurieren.
          </Text>
        </Center>
      ) : (
        <Box width="full" overflowX="auto">
          <DataTable
            typeNamePlural="Regeln"
            columns={columns}
            data={ruleRows}
            state="success"
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
          />
        </Box>
      )}

      {/* Action bar — matches Buchungskonten pattern */}
      <HStack width="full">
        <HStack spacing="16px">
          {isActiveTab && (
            <Tooltip
              label={
                !hasApprovers ? "Legen Sie zuerst einen Freigeber fest." : undefined
              }
              placement="bottom-start"
            >
              <Button
                size="sm"
                colorScheme="primary"
                onClick={handleAddRule}
                isDisabled={!hasApprovers}
              >
                Hinzufügen
              </Button>
            </Tooltip>
          )}
          <Button
            variant="link"
            size="sm"
            color={isActiveTab ? "orange.600" : "green.600"}
            _active={{ color: isActiveTab ? "orange.700" : "green.700" }}
            leftIcon={
              isActiveTab ? (
                <Box as={IconClose} boxSize="14px" />
              ) : (
                <Box as={IconCheck} boxSize="14px" />
              )
            }
            onClick={handleToggleActive}
            isDisabled={selectedIndex === undefined}
          >
            {isActiveTab ? "Deaktivieren" : "Aktivieren"}
          </Button>
        </HStack>
        <Spacer />
        <Button
          variant="link"
          size="sm"
          color="error.700"
          _active={{ color: "error.800" }}
          leftIcon={<Box as={IconTrash} boxSize="16px" />}
          onClick={handleRemoveClick}
          isDisabled={selectedIndex === undefined}
        >
          Entfernen
        </Button>
      </HStack>

      {/* Delete confirmation dialog */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="semibold">
              Regel löschen?
            </AlertDialogHeader>
            <AlertDialogBody>
              Möchten Sie diese Regel wirklich löschen?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Abbrechen
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                Löschen
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
};

export default ApprovalRulesTable;
